import { Check, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  getLibraryItemStatusLabel,
  libraryItemStatusOptions,
  type LibraryItemStatus,
} from "@/lib/library";
import { cn } from "@/lib/utils";

type LibraryStatusMenuProps = {
  status: LibraryItemStatus;
  onChange: (next: LibraryItemStatus) => void | Promise<void>;
  disabled?: boolean;
  /** Visual style of the trigger. `chip` = card chip; `badge` = detail badge. */
  variant?: "chip" | "badge";
  /** Inline style for the chip variant (tone-aware). */
  triggerStyle?: React.CSSProperties;
  /** Extra classes appended to the trigger. */
  className?: string;
  ariaLabel?: string;
};

export const LibraryStatusMenu = ({
  status,
  onChange,
  disabled = false,
  variant = "chip",
  triggerStyle,
  className,
  ariaLabel,
}: LibraryStatusMenuProps) => {
  const label = getLibraryItemStatusLabel(status);

  const triggerClass =
    variant === "chip"
      ? "inline-flex items-center gap-1 rounded-full px-2 py-0.5 align-middle text-xs font-semibold transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
      : "inline-flex items-center gap-1 rounded-md border border-border bg-secondary px-2 py-0.5 text-xs font-semibold text-secondary-foreground transition hover:bg-secondary/80 disabled:cursor-not-allowed disabled:opacity-50";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel ?? `Change status (current: ${label})`}
          className={cn(triggerClass, className)}
          style={variant === "chip" ? triggerStyle : undefined}
          onClick={(event) => event.stopPropagation()}
        >
          {label}
          <ChevronDown className="h-3 w-3 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" onClick={(event) => event.stopPropagation()}>
        <DropdownMenuLabel>Set status</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {libraryItemStatusOptions.map((option) => {
          const active = option.value === status;
          return (
            <DropdownMenuItem
              key={option.value}
              onSelect={(event) => {
                event.preventDefault();
                if (active) return;
                void onChange(option.value);
              }}
              className="flex items-center justify-between gap-3"
            >
              <span>{option.label}</span>
              {active ? <Check className="h-4 w-4 opacity-70" /> : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
